import loadable from '@loadable/component'
import Modal from 'components/global/Modal'
import { Col, Row } from 'components/grid'
import { CoreAdoptionContext } from 'context/CoreAdoption'
import { graphql, useStaticQuery } from 'gatsby'
import { GatsbyImage, IGatsbyImageData } from 'gatsby-plugin-image'
import React, { ReactNode, useContext } from 'react'
import formatUpdatedAtDate from 'utils/formatUpdatedAtDate'
import { updateAnalytics } from 'utils/updateAnalytics'
const PDFViewer = loadable(() => import('components/arc-core/PDFViewer'))

interface NoLinkResourceProps {
	props: ResourceProps
	resource: ResourceNode
}

export const NoLinkResource = ({ props, resource }: NoLinkResourceProps) => {
	return (
		<button onClick={props.onClick} className={`${props.onClick ? 'cursor-pointer' : 'cursor-default'}`}>
			{props.altImage ? (
				<img src={props.altImage} alt={resource.title} className={`${props.shadow ? 'shadow-md' : ''}`} style={{ maxHeight: props.maxHeight, width: 'auto' }} />
			) : (
				<GatsbyImage
					image={resource.cover.gatsbyImageData}
					alt={resource.title}
					style={{ maxHeight: props.maxHeight, width: 'auto' }}
					className={`${!props.noZoom && props.onClick ? 'hover-zoom mt-2' : ''} ${props.shadow ? 'shadow' : ''}`}
					objectFit="contain"
				/>
			)}
			{props.text && (
				<span data-cy={props.textTestId} className="mt-3 inline-block w-full text-center">
					{props.text}
				</span>
			)}
		</button>
	)
}

interface LinkOnlyResourceProps {
	props: ResourceProps
	resource: ResourceNode
}

export const LinkOnlyResource = ({ props, resource }: LinkOnlyResourceProps) => {
	return (
		<a href={resource.document.file.url} target="_blank" rel="noreferrer" className={`${props.linkOnlyStyle} hover:no-underline`}>
			{props.children}
		</a>
	)
}

interface ProtectedResourceProps {
	props: ResourceProps
	resource: ResourceNode
}

export const ProtectedResource = ({ resource, props }: ProtectedResourceProps) => {
	const { sessionId } = useContext(CoreAdoptionContext)
	const [modalOpen, setModalOpen] = React.useState(false)

	const handleClose = () => setModalOpen(false)
	const handleShow = () => {
		if (props.logResourceView) {
			setModalOpen(true)
			if (sessionId) updateAnalytics({ name: props.title, type: props.logResourceView.type, session_id: sessionId })
		} else setModalOpen(true)
	}
	return (
		<>
			<button onClick={handleShow}>
				{props.altImage ? (
					<img
						src={props.altImage}
						alt={resource.title}
						className={`${props.shadow ? 'shadow-md' : ''} ${props.noZoom ? '' : 'hover-zoom mt-2'}`}
						style={{ maxHeight: props.maxHeight, width: 'auto' }}
					/>
				) : (
					<GatsbyImage
						image={resource.cover && resource.cover.gatsbyImageData}
						alt={resource.title}
						style={{ maxHeight: props.maxHeight, width: 'auto' }}
						className={`${props.noZoom ? '' : 'hover-zoom mt-2'} ${props.shadow ? 'shadow' : ''}`}
						objectFit="contain"
					/>
				)}
				{props.text && (
					<span data-cy={props.textTestId} className={`w-full text-center ${props.textOnly ? '' : 'mt-3'} inline-block text-ab-100`}>
						{props.text}
					</span>
				)}
			</button>
			<Modal
				title={resource.title}
				subTitle={resource.document.updatedAt ? `Updated ${formatUpdatedAtDate(resource.document.updatedAt)}` : ''}
				isOpen={modalOpen}
				handleClose={handleClose}
				wide
			>
				<Row>
					<Col gutter="none">
						{resource.document ? (
							<PDFViewer pdf={resource.document.file.url} enablePrint={props.enablePrint} enableDownload={props.enableDownload} />
						) : (
							<GatsbyImage image={resource.cover.gatsbyImageData} alt={resource.title} />
						)}
					</Col>
				</Row>
			</Modal>
		</>
	)
}

export const ProtectedLinkResource = ({ resource, props }: ProtectedResourceProps) => {
	const [modalOpen, setModalOpen] = React.useState(false)

	const handleClose = () => setModalOpen(false)
	const handleShow = () => {
		if (props.logResourceView) {
			setModalOpen(true)
		} else setModalOpen(true)
	}
	return (
		<>
			<button onClick={handleShow}>
				{props.text && (
					<span data-cy={props.textTestId} className={`w-full text-center ${props.textOnly ? '' : 'mt-3'} inline-block text-ab-100`}>
						{props.text}
					</span>
				)}
				{props.children}
			</button>
			<Modal
				title={resource.title}
				subTitle={resource.document.updatedAt ? `Updated ${formatUpdatedAtDate(resource.document.updatedAt)}` : ''}
				isOpen={modalOpen}
				handleClose={handleClose}
				wide
			>
				<Row>
					<Col gutter="none">
						<PDFViewer pdf={resource.document.file.url} enablePrint={props.enablePrint} enableDownload={props.enableDownload} />
					</Col>
				</Row>
			</Modal>
		</>
	)
}

interface WithLinkResourceProps {
	props: ResourceProps
	resource: ResourceNode
}

export const WithLinkResource = ({ resource, props }: WithLinkResourceProps) => {
	const { sessionId } = useContext(CoreAdoptionContext)
	const handleOnClick = () => {
		if (props.logResourceView) {
			if (sessionId) updateAnalytics({ name: props.title, type: props.logResourceView.type, session_id: sessionId })
		} else {
			return true
		}
	}
	return (
		<a href={resource.document && resource.document.file.url} onClick={handleOnClick} target="_blank" rel="noopener noreferrer" className="focus:outline-none">
			{props.altImage ? (
				<img
					src={props.altImage}
					alt={resource.title}
					className={`${props.shadow ? 'shadow-md' : ''} ${props.noZoom ? '' : 'hover-zoom mt-2'}`}
					style={{ maxHeight: props.maxHeight, width: 'auto' }}
				/>
			) : (
				<GatsbyImage
					image={resource.cover.gatsbyImageData}
					alt={resource.title}
					style={{ maxHeight: props.maxHeight, width: 'auto' }}
					className={`${props.noZoom ? '' : 'hover-zoom mt-2'} ${props.shadow ? 'shadow' : ''}`}
					objectFit="contain"
				/>
			)}
			{props.text && (
				<span data-cy={props.textTestId} className="mt-3 inline-block w-full text-center text-ab-100">
					{props.text}
				</span>
			)}
		</a>
	)
}

interface WithExternalLinkResourceProps {
	props: ResourceProps
	resource: ResourceNode
}

export const WithExternalLinkResource = ({ resource, props }: WithExternalLinkResourceProps) => {
	const { sessionId } = useContext(CoreAdoptionContext)
	const handleOnClick = () => {
		if (props.logResourceView) {
			if (sessionId) updateAnalytics({ name: props.title, type: props.logResourceView.type, session_id: sessionId })
		} else {
			return true
		}
	}
	return (
		<a href={resource.externalLink && resource.externalLink} onClick={handleOnClick} target="_blank" rel="noopener noreferrer" className="focus:outline-none">
			{props.altImage ? (
				<img
					src={props.altImage}
					alt={resource.title}
					className={`${props.shadow ? 'shadow-md' : ''} ${props.noZoom ? '' : 'hover-zoom mt-2'}`}
					style={{ maxHeight: props.maxHeight, width: 'auto' }}
				/>
			) : (
				<GatsbyImage
					image={resource.cover.gatsbyImageData}
					alt={resource.title}
					style={{ maxHeight: props.maxHeight, width: 'auto' }}
					className={`${props.noZoom ? '' : 'hover-zoom mt-2'} ${props.shadow ? 'shadow' : ''}`}
					objectFit="contain"
				/>
			)}
			{props.text && (
				<span data-cy={props.textTestId} className="mt-3 inline-block w-full text-center text-ab-100">
					{props.text}
				</span>
			)}
		</a>
	)
}

interface WithDataProps {
	props: ResourceProps
	resource: ResourceNode
}

const WithData = ({ resource, props }: WithDataProps) => {
	let ReturnComponent = <></>
	if (props.noLink) ReturnComponent = <NoLinkResource resource={resource} props={props} />
	if (props.protected) ReturnComponent = <ProtectedResource resource={resource} props={props} />
	if (props.protectedLink) ReturnComponent = <ProtectedLinkResource resource={resource} props={props} />
	if (props.linkOnly) ReturnComponent = <LinkOnlyResource resource={resource} props={props} />
	if (props.external) ReturnComponent = <WithExternalLinkResource resource={resource} props={props} />
	else if (!props.noLink && !props.protected && !props.protectedLink && !props.linkOnly) ReturnComponent = <WithLinkResource resource={resource} props={props} />
	return ReturnComponent
}

interface WithoutDataProps {
	props: ResourceProps
}

const WithoutData = ({ props }: WithoutDataProps) => {
	return <div className="flex h-56 w-full items-center justify-center bg-gray-200 text-center font-semibold text-gray-600 shadow">{props.title}</div>
}

const Resource = (props: ResourceProps) => {
	const data = useStaticQuery<ResourceQuery>(query).allContentfulResource.edges.filter((e) => e.node.title === props.title)
	const RenderComponent = data.length > 0 ? <WithData props={props} resource={data[0].node} /> : <WithoutData props={props} />
	return RenderComponent
}

export default Resource

interface ResourceProps {
	/**
	 * This must match a Resource content type from Contentful
	 */
	title: string
	/**
	 * Optional text link that will show up below the image
	 */
	text?: string
	/**
	 * Will open the resource in a PDF viewer modal window instead of the default behavior
	 */
	protected?: boolean
	/**
	 * Will open the resource in a PDF viewer modal window instead of the default behavior but will not show an image preview
	 */
	protectedLink?: boolean
	/**
	 * Will allow the protected resource to be printed from the options menu
	 */
	enablePrint?: boolean
	/**
	 * Will allow the protected resource to be downloaded from the options menu
	 */
	enableDownload?: boolean
	/**
	 * Will apply a box-shadow to the image. Useful for images that have white with no defined border
	 */
	shadow?: boolean
	/**
	 * Used to scale down the image
	 */
	maxHeight?: number
	/**
	 * Used to render only the image and not the link to the resource document
	 */
	noLink?: boolean
	/**
	 * When true, this will return an anchor tag which can accept children
	 */
	linkOnly?: boolean
	/**
	 * When true, this will return something...I have to check into this
	 */
	textOnly?: string
	/**
	 * When true, this will return an anchor tag with the externalLink value from the resource in Contentful
	 */
	external?: boolean
	/**
	 * If using the linkOnly prop, use this to pass classNames to style the link
	 */
	linkOnlyStyle?: string
	/**
	 * When true, this will not add the hover zoom effect to the image
	 */
	noZoom?: boolean
	/**
	 * If you would like to use a different cover image than the one that comes from Contentful, pass it here
	 */
	altImage?: string
	/**
	 * If you would like to specify a different onClick action than the default
	 */
	onClick?: () => any
	/**
	 * If you would like to specify a different onClick action than the default
	 */
	logResourceView?: { type: 'document' | 'resource' }
	textTestId?: string
	children?: ReactNode
}

interface ResourceNode {
	title: string
	cover: {
		gatsbyImageData: IGatsbyImageData
	}
	document: {
		updatedAt: string
		file: {
			url: string
		}
	}
	externalLink: string
}

interface ResourceQueryNode {
	node: ResourceNode
}

interface ResourceQuery {
	allContentfulResource: {
		edges: ResourceQueryNode[]
	}
}

const query = graphql`
	query {
		allContentfulResource {
			edges {
				node {
					title
					cover {
						gatsbyImageData(height: 800)
					}
					document {
						updatedAt
						file {
							url
						}
					}
					externalLink
				}
			}
		}
	}
`
